/*
  Small text helpers. They only make a first suggestion for a box the person can change; the server makes the
  real codes and checks them (2.4.2).
*/

/** Lower case ASCII words joined by underscores: "Shaft power" becomes "shaft_power". */
export function snake(text: string): string {
  return text
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

/** A short key for a chapter or one of its sections, made from its title. */
export function keyFromTitle(title: string): string {
  return snake(title).slice(0, 60);
}

/** Split a text with commas or line breaks into trimmed, non-empty entries without repeats. */
export function splitList(text: string): string[] {
  const seen = new Set<string>();
  for (const part of text.split(/[\n,;]+/)) {
    const entry = part.trim();
    if (entry) seen.add(entry);
  }
  return [...seen];
}

/** True for a whole number of at least `min` written in a box. */
export function isWholeNumber(text: string, min = 1): boolean {
  return /^\d+$/.test(text.trim()) && Number(text.trim()) >= min;
}

/** A number of bytes in words: "1.4 MB". */
export function sizeWords(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "";
  if (bytes < 1024) return `${bytes} bytes`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb < 10 ? kb.toFixed(1) : Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
}

/** "1 thing" or "3 things". */
export function plural(count: number, one: string, many = `${one}s`): string {
  return `${count} ${count === 1 ? one : many}`;
}

/** The time of an event in the reader's own style, without the milliseconds. */
export function whenWords(ts: string | null | undefined): string {
  if (!ts) return "";
  const date = new Date(ts);
  if (Number.isNaN(date.getTime())) return ts;
  return date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

/** A value from a loose JSON object as text, or "". */
export function textOf(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return "";
}
